import { stripMarkdown } from "@/components/molecules/markdown";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { UnsureNameMark } from "@/components/molecules/unsure-name-mark";
import {
  deadlineWasPromised,
  messageIsLate,
  type ClientMessage,
} from "@/features/messages/types";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  ClockIcon,
  PhoneCallIcon,
  UserIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { format, isThisYear, isToday } from "date-fns";
import type { Locale } from "date-fns";
import { useCategoryLabels } from "./categories";

/**
 * A date as short as the row allows: the hour today, the day this year, the
 * full date before that.
 */
export const shortDate = (timestamp: number, locale: Locale) =>
  format(
    timestamp,
    isToday(timestamp) ? "p" : isThisYear(timestamp) ? "d MMM" : "P",
    { locale },
  );

/**
 * What a request's state reads as: an answer written on a request nobody closed is
 * worth its own state — the customer already has what they asked for, the team
 * still owes a decision.
 */
export const messageStatusKey = (message: ClientMessage) =>
  message.status === "pending"
    ? message.reply
      ? "answered"
      : "pending"
    : message.resolution || "dropped";

/**
 * One row of the Messagerie, a mail client's message row: who asked, what about,
 * what they said, and what makes it urgent — its deadline (promised to the
 * customer, or earned by its urgency), the urgency the review pass read in the
 * call, and the category it was filed under.
 */
export function MessageListItem({
  message,
  selected,
  checked,
  onSelect,
  onCheck,
}: {
  message: ClientMessage;
  selected: boolean;
  checked: boolean;
  onSelect: (message: ClientMessage) => void;
  onCheck: (message: ClientMessage, checked: boolean) => void;
}) {
  const { t } = useTranslation("messages");
  const locale = useDateFnsLocale();
  const { labelOf, colorOf } = useCategoryLabels();
  const pending = message.status === "pending";
  const late = messageIsLate(message);
  const urgent = pending && message.urgency === "high";

  // A handled request is dated by when it was closed: that is the order the
  // "Traitées" list is read in.
  const when =
    !pending && message.handled_at ? message.handled_at : message.created_at;

  return (
    <div
      className={cn(
        "flex items-stretch border-b border-l-2 transition-colors",
        selected
          ? "border-l-primary bg-slate-100 dark:bg-slate-900"
          : checked
            ? "border-l-transparent bg-primary/5"
            : "border-l-transparent hover:bg-slate-50 dark:hover:bg-slate-900",
      )}
    >
      <label className="flex cursor-pointer items-start py-3 pr-1 pl-3">
        <Checkbox
          checked={checked}
          onCheckedChange={(value) => onCheck(message, value === true)}
          aria-label={message.subject}
          className="mt-0.5"
        />
      </label>
      <button
        type="button"
        onClick={() => onSelect(message)}
        aria-current={selected}
        className="min-w-0 flex-1 py-3 pr-3 pl-2 text-left"
      >
        <div className="flex items-center gap-2">
          {urgent && (
            <WarningIcon
              className="h-4 w-4 shrink-0 text-red-600"
              weight="fill"
              aria-label={t("table.urgent")}
            />
          )}
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-sm",
              pending ? "font-semibold" : "font-medium text-muted-foreground",
            )}
          >
            {message.customer_name ||
              message.phone_number ||
              t("table.unknownCustomer")}
            {message.customer_name && message.unsure_customer_name && (
              <UnsureNameMark />
            )}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {when ? shortDate(when, locale) : ""}
          </span>
        </div>

        <div
          className={cn(
            "mt-1 truncate text-sm",
            !pending && "text-muted-foreground",
          )}
        >
          {message.subject}
        </div>

        {message.body && (
          <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {stripMarkdown(message.body)}
          </div>
        )}

        <div className="mt-2 flex items-center gap-3 overflow-hidden text-xs">
          {pending ? (
            <>
              {!!message.due_at && (
                <span
                  className={cn(
                    "flex shrink-0 items-center gap-1",
                    late
                      ? "font-medium text-amber-600 dark:text-amber-400"
                      : "text-muted-foreground",
                  )}
                >
                  <ClockIcon className="h-3.5 w-3.5" weight="fill" />
                  {late
                    ? t("table.late")
                    : t(
                        deadlineWasPromised(message)
                          ? "list.dueOn"
                          : "list.deadlineOn",
                        { date: shortDate(message.due_at, locale) },
                      )}
                </span>
              )}
              {!!message.reply && (
                <span className="shrink-0 text-muted-foreground">
                  {t("status.answered")}
                </span>
              )}
            </>
          ) : (
            <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
              {message.resolution === "delegated" && (
                <PhoneCallIcon className="h-3.5 w-3.5" weight="fill" />
              )}
              {t(`status.${messageStatusKey(message)}`)}
            </span>
          )}
          <Badge
            color={colorOf(message.category)}
            className="shrink-0 px-1.5 py-0 font-normal"
          >
            {labelOf(message.category)}
          </Badge>
          {message.assigned_member_name && (
            <span className="ml-auto flex min-w-0 items-center gap-1 text-muted-foreground">
              <UserIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{message.assigned_member_name}</span>
            </span>
          )}
        </div>
      </button>
    </div>
  );
}
